<template>
  <div class="cost-center-attribution-editor">
    <div class="editor-header mb-4">
      <h3 class="mb-2">{{ $t('cost-center.edit-allocations-title') }}</h3>
      <div class="entity-info d-flex align-center">
        <v-icon color="primary" class="mr-2">{{ getEntityIcon() }}</v-icon>
        <h4>{{ getEntityName() }}</h4>
      </div>
    </div>

    <!-- Allocations Table -->
    <div class="allocations-table mb-4">
      <v-simple-table dense>
        <template v-slot:default>
          <thead>
            <tr>
              <th>{{ $tc('cost-center.cost-center') }}</th>
              <th class="text-center">{{ $t('cost-center.before') }}</th>
              <th class="text-center">{{ $t('cost-center.after') }}</th>
            </tr>
          </thead>
          <tbody>
            <!-- Empty state when no cost centers -->
            <tr v-if="currentAllocations.length === 0" class="empty-state-row">
              <td colspan="3" class="text-center grey--text py-4">
                {{ $t('cost-center.no-allocations') }}
              </td>
            </tr>

            <!-- Cost center rows -->
            <tr
              v-for="(alloc, index) in currentAllocations"
              :key="index"
              :class="{ 'deleted-allocation': alloc.isDeleted }"
            >
              <td>
                <div class="d-flex align-center">
                  <span :class="{ 'text-decoration-line-through': alloc.isDeleted }">
                    {{ alloc.costCenterName }}
                  </span>
                  <v-chip v-if="alloc.isNew && !alloc.isDeleted" x-small color="success" class="ml-2">
                    {{ $t('new') }}
                  </v-chip>
                  <v-chip v-if="alloc.isDeleted" x-small color="error" class="ml-2">
                    {{ $t('cost-center.deleted') }}
                  </v-chip>
                  <v-btn
                    icon
                    x-small
                    class="ml-2"
                    :disabled="saving"
                    @click="toggleDeleteAllocation(index)"
                  >
                    <v-icon v-if="!alloc.isDeleted" x-small color="error">mdi-close</v-icon>
                    <v-icon v-else x-small color="success">mdi-undo</v-icon>
                  </v-btn>
                </div>
              </td>
              <td class="text-center">{{ alloc.beforePercent.toFixed(1) }}%</td>
              <td class="text-center">
                <v-text-field
                  v-model.number="alloc.sharePercent"
                  type="number"
                  min="0"
                  max="100"
                  step="0.01"
                  suffix="%"
                  dense
                  hide-details
                  outlined
                  :disabled="alloc.isDeleted || saving"
                  @input="onPercentChange(alloc)"
                />
              </td>
            </tr>
          </tbody>
        </template>
      </v-simple-table>

      <!-- Add Cost Center -->
      <v-btn
        v-if="availableCostCenters.length > 0"
        small
        text
        block
        class="mt-2"
        :disabled="saving"
        @click="openAddCostCenter"
      >
        <v-icon left small>mdi-plus</v-icon>
        {{ $t('cost-center.add-cost-center') }}
      </v-btn>
    </div>

    <!-- Quick Actions (only if multiple active cost centers) -->
    <div v-if="activeAllocationsCount >= 2" class="quick-actions mb-4">
      <v-btn small outlined block class="mb-2" :disabled="saving" @click="distributeEqually">
        <v-icon left small>mdi-chart-pie</v-icon>
        {{ $t('cost-center.distribute-equally') }}
      </v-btn>
      <v-btn small outlined block class="mb-2" :disabled="saving" @click="keepRatios">
        <v-icon left small>mdi-chart-line</v-icon>
        {{ $t('cost-center.keep-ratios') }}
      </v-btn>
      <v-btn
        v-if="hasZeroAllocations"
        small
        outlined
        block
        :disabled="!canCompleteZeros || saving"
        @click="completeZeros"
      >
        <v-icon left small>mdi-percent</v-icon>
        {{ $t('cost-center.complete-zeros') }}
      </v-btn>
    </div>

    <!-- Visual Progress Bar (only if multiple active cost centers) -->
    <div v-if="activeAllocationsCount >= 2" class="visual-progress mb-4">
      <div class="progress-label mb-1">
        {{ $t('cost-center.total') }} :
        <span :class="totalClass">{{ totalPercent.toFixed(2) }}%</span>
        <v-icon v-if="isValid" small color="success">mdi-check-circle</v-icon>
        <v-icon v-else small color="warning">mdi-alert-circle</v-icon>
      </div>

      <v-progress-linear
        :value="Math.min(totalPercent, 100)"
        height="20"
        :color="progressColor"
        striped
      >
        <template v-slot:default>
          <strong class="white--text">{{ totalPercent.toFixed(1) }}%</strong>
        </template>
      </v-progress-linear>
    </div>

    <!-- Validation Messages (only if multiple active cost centers) -->
    <v-alert v-if="activeAllocationsCount >= 2 && !isValid" type="warning" dense text class="mb-4">
      {{ validationMessage }}
    </v-alert>

    <!-- Actions -->
    <div class="editor-actions">
      <v-btn small text @click="cancel" :disabled="saving">
        {{ $t('cancel') }}
      </v-btn>
      <v-btn
        small
        color="primary"
        :disabled="!isValid || !hasChanges || saving"
        :loading="saving"
        @click="save"
      >
        <v-icon left small>mdi-check</v-icon>
        {{ $t('cost-center.apply-changes') }}
      </v-btn>
    </div>

    <!-- Add Cost Center Dialog -->
    <v-dialog v-model="showAddCostCenter" max-width="400">
      <v-card>
        <v-card-title>{{ $t('cost-center.add-cost-center') }}</v-card-title>
        <v-card-text>
          <v-select
            v-model="selectedCostCenterId"
            :items="availableCostCenters"
            item-text="name"
            item-value="id"
            :label="$t('cost-center.select-cost-center')"
            outlined
            dense
          />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text small @click="showAddCostCenter = false">
            {{ $t('cancel') }}
          </v-btn>
          <v-btn
            color="primary"
            small
            :disabled="!selectedCostCenterId"
            @click="addCostCenter"
          >
            {{ $t('add') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
export default {
  props: {
    organizationId: {
      type: String,
      required: true
    },
    entity: {
      type: Object,
      required: true
    },
    costCenters: {
      type: Array,
      default: () => []
    },
    saving: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      currentAllocations: [],
      showAddCostCenter: false,
      selectedCostCenterId: null,
      totalPercent: 0,
      isValid: false,
      validationMessage: ''
    }
  },

  computed: {
    availableCostCenters() {
      // Exclude cost centers already in the list (not deleted)
      const currentCCIds = this.currentAllocations
        .filter(a => !a.isDeleted)
        .map(a => a.costCenterId)
      return this.costCenters.filter(cc => !currentCCIds.includes(cc.id))
    },

    hasInitialCostCenters() {
      // Check if entity had cost centers initially
      return this.currentAllocations.some(a => a.beforePercent > 0)
    },

    activeAllocationsCount() {
      // Count non-deleted allocations
      return this.currentAllocations.filter(a => !a.isDeleted).length
    },

    hasZeroAllocations() {
      const activeAllocations = this.currentAllocations.filter(a => !a.isDeleted)
      return activeAllocations.some(a => a.sharePercent === 0 || !a.sharePercent)
    },

    availableSpaceForZeros() {
      const activeAllocations = this.currentAllocations.filter(a => !a.isDeleted)
      const nonZeros = activeAllocations.filter(a => a.sharePercent > 0)
      const usedSpace = nonZeros.reduce((sum, a) => sum + a.sharePercent, 0)
      return Math.round((100 - usedSpace) * 100) / 100
    },

    canCompleteZeros() {
      return this.hasZeroAllocations && this.availableSpaceForZeros > 0
    },

    hasChanges() {
      // Check if there are any modifications to save
      return this.currentAllocations.some(alloc => {
        // New cost center added (beforePercent was 0)
        if (alloc.beforePercent === 0 && alloc.sharePercent > 0) return true

        // Cost center deleted
        if (alloc.isDeleted) return true

        // Percentage changed
        if (Math.abs(alloc.sharePercent - alloc.beforePercent) > 0.001) return true

        return false
      })
    },

    totalClass() {
      return this.isValid ? 'success--text' : 'warning--text'
    },

    progressColor() {
      if (this.isValid) return 'success'
      if (this.totalPercent > 100) return 'error'
      return 'warning'
    }
  },

  watch: {
    entity: {
      handler() {
        this.initializeAllocations()
      },
      deep: true
    }
  },

  mounted() {
    this.initializeAllocations()
  },

  methods: {
    initializeAllocations() {
      if (!this.entity) return

      // Start with existing cost centers
      this.currentAllocations = (this.entity.costCenters || []).map(cc => ({
        costCenterId: cc.costCenterId || cc.id,
        costCenterName: cc.costCenterName || cc.name,
        beforePercent: cc.sharePercent || 0,
        sharePercent: cc.sharePercent || 0,
        isDeleted: false
      }))

      // If action is 'assign', add the target cost center
      if (this.entity.action === 'assign' && this.entity.targetCostCenter) {
        // Check if target cost center is not already in the list
        const exists = this.currentAllocations.some(
          alloc => alloc.costCenterId === this.entity.targetCostCenter.id
        )

        if (!exists) {
          this.currentAllocations.push({
            costCenterId: this.entity.targetCostCenter.id,
            costCenterName: this.entity.targetCostCenter.name,
            beforePercent: 0,
            sharePercent: 0,
            isNew: true, // Mark as new for visual indication
            isDeleted: false
          })
        }
      }
      this.validateTotal()
    },

    getEntityIcon() {
      const icons = {
        'member': 'mdi-account',
        'charging-point': 'mdi-ev-station',
        'meter-device': 'mdi-gauge'
      }
      return icons[this.entity?.type] || 'mdi-help-circle'
    },

    getEntityName() {
      if (!this.entity) return ''

      if (this.entity.type === 'member') {
        const fullName = `${this.entity.customer?.firstname || ''} ${this.entity.customer?.lastname || ''}`.trim()
        return fullName || this.entity.customer?.evcoId || '-'
      }
      return this.entity.name || this.entity.identifier || '-'
    },

    onPercentChange(alloc) {
      // Round to 2 decimals
      alloc.sharePercent = Math.round(alloc.sharePercent * 100) / 100
      this.validateTotal()
    },

    validateTotal() {
      // Filter out deleted cost centers for validation
      const activeAllocations = this.currentAllocations.filter(a => !a.isDeleted)

      // If no active cost centers
      if (activeAllocations.length === 0) {
        this.totalPercent = 0
        this.isValid = true // Always valid to have no cost centers
        this.validationMessage = ''
        return
      }

      // Calculate total
      this.totalPercent = activeAllocations.reduce((sum, alloc) => {
        return sum + (parseFloat(alloc.sharePercent) || 0)
      }, 0)

      // Round total to 2 decimals
      this.totalPercent = Math.round(this.totalPercent * 100) / 100

      // If only one active cost center, it's always valid (regardless of percentage)
      if (activeAllocations.length === 1) {
        this.isValid = true
        this.validationMessage = ''
        return
      }

      // For multiple cost centers: check if total is 100%
      const diff = Math.abs(this.totalPercent - 100)
      this.isValid = diff <= 0.001

      // Generate validation message
      if (diff > 0.001) {
        if (this.totalPercent < 100) {
          this.validationMessage = this.$t('cost-center.total-below-100', {
            diff: (100 - this.totalPercent).toFixed(2)
          })
        } else {
          this.validationMessage = this.$t('cost-center.total-above-100', {
            diff: (this.totalPercent - 100).toFixed(2)
          })
        }
      } else {
        this.validationMessage = ''
      }

      // Check for 0% allocations (only on active allocations with multiple cost centers)
      const hasZero = activeAllocations.some(a => a.sharePercent === 0 || !a.sharePercent)
      if (hasZero) {
        this.isValid = false
        this.validationMessage = this.$t('cost-center.zero-percent-forbidden')
      }
    },

    distributeEqually() {
      const activeAllocations = this.currentAllocations.filter(a => !a.isDeleted)
      const count = activeAllocations.length
      if (count === 0) return

      const equalShare = Math.round((100 / count) * 100) / 100

      // Assign equal share to all
      activeAllocations.forEach(alloc => {
        alloc.sharePercent = equalShare
      })

      // Calculate the total after rounding
      const total = activeAllocations.reduce((sum, a) => sum + a.sharePercent, 0)

      // Adjust the last cost center to make exactly 100%
      if (activeAllocations.length > 0) {
        const diff = 100 - total
        activeAllocations[activeAllocations.length - 1].sharePercent =
          Math.round((activeAllocations[activeAllocations.length - 1].sharePercent + diff) * 100) / 100
      }

      this.validateTotal()
    },

    keepRatios() {
      const activeAllocations = this.currentAllocations.filter(a => !a.isDeleted)
      if (this.totalPercent === 0) return

      const factor = 100 / this.totalPercent
      activeAllocations.forEach(alloc => {
        alloc.sharePercent = Math.round((alloc.sharePercent * factor) * 100) / 100
      })

      // Calculate the total after rounding
      const total = activeAllocations.reduce((sum, a) => sum + a.sharePercent, 0)

      // Adjust the last cost center to make exactly 100%
      if (activeAllocations.length > 0) {
        const diff = 100 - total
        activeAllocations[activeAllocations.length - 1].sharePercent =
          Math.round((activeAllocations[activeAllocations.length - 1].sharePercent + diff) * 100) / 100
      }

      this.validateTotal()
    },

    completeZeros() {
      const activeAllocations = this.currentAllocations.filter(a => !a.isDeleted)

      // Separate zeros and non-zeros
      const zeros = activeAllocations.filter(a => a.sharePercent === 0 || !a.sharePercent)
      const nonZeros = activeAllocations.filter(a => a.sharePercent > 0)

      if (zeros.length === 0) return

      // Calculate available space
      const usedSpace = nonZeros.reduce((sum, a) => sum + a.sharePercent, 0)
      const availableSpace = 100 - usedSpace

      if (availableSpace <= 0) return // Safety check (button should be disabled)

      // Distribute available space equally among zeros
      const sharePerZero = availableSpace / zeros.length

      zeros.forEach(alloc => {
        alloc.sharePercent = Math.round(sharePerZero * 100) / 100
      })

      // Adjust the last zero to reach exactly 100%
      const newTotal = activeAllocations.reduce((sum, a) => sum + a.sharePercent, 0)
      if (zeros.length > 0) {
        const diff = 100 - newTotal
        zeros[zeros.length - 1].sharePercent =
          Math.round((zeros[zeros.length - 1].sharePercent + diff) * 100) / 100
      }

      this.validateTotal()
    },

    toggleDeleteAllocation(index) {
      const alloc = this.currentAllocations[index]

      // If the cost center is new (beforePercent === 0), delete it directly
      if (alloc.beforePercent === 0) {
        this.currentAllocations.splice(index, 1)
        this.validateTotal()
        return
      }

      // Otherwise, it's an existing cost center: toggle soft delete
      alloc.isDeleted = !alloc.isDeleted

      // If we're undeleting, reset the sharePercent to beforePercent
      if (!alloc.isDeleted) {
        alloc.sharePercent = alloc.beforePercent
      }

      this.validateTotal()
    },

    openAddCostCenter() {
      // Pre-select the first available cost center
      if (this.availableCostCenters.length > 0) {
        this.selectedCostCenterId = this.availableCostCenters[0].id
      }
      this.showAddCostCenter = true
    },

    addCostCenter() {
      if (!this.selectedCostCenterId) return

      const cc = this.costCenters.find(c => c.id === this.selectedCostCenterId)
      if (!cc) return

      // Check if this cost center already exists in the list (even if soft-deleted)
      const existingAllocation = this.currentAllocations.find(
        a => a.costCenterId === this.selectedCostCenterId
      )

      if (existingAllocation) {
        // Cost center already exists (probably soft-deleted), restore it instead of creating duplicate
        if (existingAllocation.isDeleted) {
          existingAllocation.isDeleted = false
          // Restore to its beforePercent value
          existingAllocation.sharePercent = existingAllocation.beforePercent
        }
        // If not deleted, it means it's already active - this shouldn't happen but just in case
      } else {
        // Cost center doesn't exist yet, add it as new
        const hadNoCostCenters = this.currentAllocations.length === 0

        this.currentAllocations.push({
          costCenterId: cc.id,
          costCenterName: cc.name,
          beforePercent: 0,
          sharePercent: 0,
          isDeleted: false
        })

        // Smart auto-distribution
        if (hadNoCostCenters) {
          // Entity had no cost center: assign 100% to the new one
          this.currentAllocations[0].sharePercent = 100
        } else {
          // Entity already had cost centers: NEW stays at 0%
          // User must manually adjust OR use "Complete zeros" or "Distribute equally"
          // (no automatic redistribution)
        }
      }

      this.selectedCostCenterId = null
      this.showAddCostCenter = false
      this.validateTotal()
    },

    save() {
      if (!this.isValid) return

      this.$emit('save', {
        entity: this.entity,
        allocations: this.currentAllocations
      })
    },

    cancel() {
      this.$emit('cancel')
    }
  }
}
</script>

<style lang="scss">
.cost-center-attribution-editor {
  max-width: 100%;
  min-width: 0;
  .editor-header {
    h3 {
      margin: 0;
      font-size: 16px;
      font-weight: 600;
    }
  }

  .quick-actions {
    border: 1px solid #e0e0e0;
    border-radius: 4px;
    padding: 12px;
  }

  .allocations-table {
    ::v-deep table {
      th {
        font-size: 12px;
        font-weight: 600;
      }

      td {
        font-size: 13px;
      }

      input {
        font-size: 13px;
      }
    }

    .deleted-allocation {
      opacity: 0.6;
      background-color: #ffebee;

      td {
        color: #c62828;
      }
    }
  }

  .visual-progress {
    .progress-label {
      display: flex;
      align-items: center;
      gap: 8px;
      font-weight: 500;
    }
  }

  .editor-actions {
    display: flex;
    justify-content: space-between;
    gap: 8px;
  }
}
</style>
